import { useCallback, useState } from "react";

import {
  useLazyGetDeviceCommandStatusQuery,
  useSendDeviceCommandMutation,
} from "@/features/devices/devicesApi";
import { DeviceDetailResponse } from "@/features/devices/deviceDetailDisplay";
import {
  DownloadSpeedTestUiState,
  TEST_DOWNLOAD_SPEED_COMMAND,
  speedTestDisabledReason,
} from "@/features/devices/downloadSpeedTest";
import { Device } from "@/features/devices/types";
import { waitForDeviceCommandOperatorResult } from "@/features/devices/waitForDeviceCommandOperatorResult";

/**
 * Click-only remote speed test. Never invoked from expand.
 * Uses the same Fleet command that Assistant measureNow() handles.
 */
export function useDownloadSpeedTest(args: {
  device: Device;
  detailResponse?: DeviceDetailResponse;
  refetch?: () => Promise<unknown>;
}): {
  uiState: DownloadSpeedTestUiState;
  disabledReason: string | null;
  runTest: () => Promise<void>;
} {
  const { device, detailResponse, refetch } = args;
  const [sendDeviceCommand] = useSendDeviceCommandMutation();
  const [getCommandStatus] = useLazyGetDeviceCommandStatusQuery();
  const [uiState, setUiState] = useState<DownloadSpeedTestUiState>("idle");

  const snapshot = detailResponse?.detail?.internet?.downloadTest;
  const disabledReason = speedTestDisabledReason({
    device,
    snapshot,
    uiState,
  });

  const runTest = useCallback(async () => {
    if (disabledReason) return;
    setUiState("running");
    try {
      const sent = await sendDeviceCommand({
        deviceId: device.id,
        command: TEST_DOWNLOAD_SPEED_COMMAND,
      }).unwrap();
      const commandId = sent.commandId;
      const outcome = await waitForDeviceCommandOperatorResult({
        commandId,
        command: TEST_DOWNLOAD_SPEED_COMMAND,
        getStatus: async (id) => {
          const result = await getCommandStatus({ commandId: id }).unwrap();
          return result?.command || null;
        },
      });
      if (outcome === "successful") {
        setUiState("successful");
        await refetch?.();
        return;
      }
      const status = commandId
        ? await getCommandStatus({ commandId }).unwrap().catch(() => null)
        : null;
      const terminal = status?.command?.terminalOutcome;
      setUiState(terminal === "timed_out" || !commandId ? "timed_out" : "failed");
      await refetch?.();
    } catch (error) {
      const data =
        error && typeof error === "object" && "data" in error
          ? (error as { data?: { code?: string } }).data
          : undefined;
      if (data?.code === "speed_test_cooldown" || data?.code === "speed_test_in_flight") {
        setUiState("idle");
        return;
      }
      setUiState("failed");
    }
  }, [device.id, disabledReason, getCommandStatus, refetch, sendDeviceCommand]);

  return { uiState, disabledReason, runTest };
}
